import gql from 'graphql-tag';
import { getGraphQLClientForPivotWithToken } from 'utils/apiRequests';

//Below two are helper functions for fieldname errors for graphql 2.4.0
function lowercaseFirstChar(obj) {
	const newObj = {};
	for (let key in obj) {
		if (obj.hasOwnProperty(key)) {
			const value = obj[key];
			const newKey = key.charAt(0).toLowerCase() + key.slice(1);
			if (typeof value === 'object' && !Array.isArray(value)) {
				// Recursively lowercase first character of field names of nested objects
				newObj[newKey] = lowercaseFirstChar(value);
			} else {
				newObj[newKey] = value;
			}
		}
	}

	// Remove the "filterTo" and "dateTo" property if it exists
	delete newObj.filterTo;
	delete newObj.dateTo;

	return newObj;
}
function createColsHash(colDefs) {
	if (!colDefs) {
		return null;
	}
	var parts = [];
	colDefs.forEach(function(colDef) {
		if (colDef.children) {
			parts.push(colDef.groupId);
			parts.push('[' + createColsHash(colDef.children) + ']');
		} else {
			parts.push(colDef.colId);
			// headerName can change if the aggFunc was changed in a value col. if we didn't
			// do this, then the grid would not pick up on new header names as we move from
			// eg min to max.
			if (colDef.headerName) {
				parts.push(colDef.headerName);
			}
		}
	});
	return parts.join(',');
}
function capitalizeFieldNames(arr) {
	// Iterate over each object in the array
	return arr.map(obj => {
		const newObj = {};
		// Iterate over each key in the object
		for (let key in obj) {
			if (obj.hasOwnProperty(key)) {
				// Check if the key contains "custom_data"
				if (
					key === 'pO_BASE_SPEND' ||
					key === 'nET_VALUE_BASE_GBP' ||
					key === 'nET_VALUE_BASE_USD' ||
					key === 'nET_VALUE_BASE_EUR'
				) {
					const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
					newObj[capitalizedKey] = obj[key]?.toLocaleString();
				} else if (key.includes('custom_data')) {
					newObj[key] = obj[key]; // No capitalization for "custom_data" keys
				} else if (key.endsWith('_count')) {
					const newKey = key.slice(0, -6); // Remove the '_count' suffix
					const capitalizedKey =
						newKey.charAt(0).toUpperCase() + newKey.slice(1);
					newObj[capitalizedKey] = obj[key];
				} else {
					// Capitalize the first letter of the key
					if (key === 'sOURCEABLE') {
						// Capitalize the first letter of the key
						const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
						// Assign the value to the new capitalized key
						if (obj[key] === true) {
							newObj[capitalizedKey] = 'true';
						} else if (obj[key] === false) {
							newObj[capitalizedKey] = 'false';
						} else {
							newObj[capitalizedKey] = null;
						}
					} else if (key === 'pO_CREATION_DATE') {
						// Capitalize the first letter of the key
						const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
						// Assign the value to the new capitalized key
						const formattedDate = new Date(obj[key]).toLocaleDateString(
							'en-GB',
							{ day: '2-digit', month: '2-digit', year: 'numeric' }
						);
						newObj[capitalizedKey] = formattedDate;
					} else {
						const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
						// Assign the value to the new capitalized key
						newObj[capitalizedKey] = obj[key];
					}
				}
			}
		}
		return newObj;
	});
}

const query = (request, columns) => {
	// const fields=columns.map(col=>col.fields);
	const valueColsFields = request.valueCols.map(
		value =>
			`${value.field.charAt(0).toLowerCase() +
				value.field.slice(1) +
				'_' +
				value.aggFunc}`
	);
	const nestedCols = columns
		.filter(col => col.children)
		.map(childrenfields => childrenfields.children)
		.flat()
		.map(col => `${col.field?.charAt(0).toLowerCase()}${col.field?.slice(1)}`);
	const normalcols = columns
		.map(col => `${col.field?.charAt(0).toLowerCase()}${col.field?.slice(1)}`)
		.filter(el => el !== 'undefinedundefined')
		.filter(el => el !== 'pO_ID_AP');
	const fields = normalcols.concat(nestedCols);
	return {
		query: gql`
      query pivotdata(
          $start: Int!, 
          $end: Int!, 
          $sortModel: [SortModelType!], 
          $groupKeys: [String!],
          $rowGroups:[RowGroupModelType!],
          $filterModel:[FilterModelType!],
		  $valueCols:[ValueColsModelType!]
        ) {
          agGridData(
          startRow: $start,    
          endRow: $end,
          sorting: $sortModel,
          groupKeys: $groupKeys,  
          rowGroups:$rowGroups,
          filterModel:$filterModel,
		  valueCols:$valueCols
        ) {
        ${[...fields, ...valueColsFields]}
        }
      }
    `,
		variables: {
			start: request.startRow,
			end: request.endRow,
			sortModel: request.sortModel,
			groups: request.rowGroupCols,
			groupKeys: request.groupKeys,
			valueCols: request.valueCols,
			rowGroups: request.rowGroupCols,
			filterModel: [lowercaseFirstChar(request.filterModel)],
		},
	};
};
const pivotquery = (request, columns) => {
	// const fields=columns.map(col=>col.fields);
	const nestedCols = columns
		.filter(col => col.children)
		.map(childrenfields => childrenfields.children)
		.flat()
		.map(col => `${col.field?.charAt(0).toLowerCase()}${col.field?.slice(1)}`);
	const normalcols = columns
		.map(col => `${col.field?.charAt(0).toLowerCase()}${col.field?.slice(1)}`)
		.filter(el => el !== 'undefinedundefined')
		.filter(el => el !== 'pO_ID_AP');

	const fields = normalcols.concat(nestedCols);
	const valueColsFields = request.valueCols.map(
		value =>
			`${value.field.charAt(0).toLowerCase() +
				value.field.slice(1) +
				'_' +
				value.aggFunc}`
	);
	const valueColsFiedsSort = request.valueCols.map(col => {
		return `${col.field}`;
	});
	const sortModel = request.sortModel.filter(sort =>
		valueColsFiedsSort.includes(sort.colId)
	);

	return {
		query: gql`
      query pivotdata(
          $start: Int!, 
          $end: Int!, 
          $sortModel: [SortModelType!], 
          $groupKeys: [String!],
          $rowGroups:[RowGroupModelType!],
          $filterModel:[FilterModelType!],
		  $valueCols:[ValueColsModelType!],
		  $pivotCols:[PivotColsModelType!],
		  $pivotColsSort:[SortModelType!]
        ) {
			pivotData(
          startRow: $start,    
          endRow: $end,
          sorting: $sortModel,
          groupKeys: $groupKeys,  
          rowGroups:$rowGroups,
          filterModel:$filterModel,
		  valueCols:$valueCols,
		  pivotCols:$pivotCols,
		  pivotColsSort:$pivotColsSort
        ) {
        ${[...fields, ...valueColsFields, 'data']}
        }
      }
    `,
		variables: {
			start: request.startRow,
			end: request.endRow,
			sortModel: sortModel,
			groups: request.rowGroupCols,
			groupKeys: request.groupKeys,
			valueCols: request.valueCols,
			rowGroups: request.rowGroupCols,
			filterModel: [lowercaseFirstChar(request.filterModel)],
			pivotCols: request.pivotCols,
			pivotColsSort: request.sortModel,
		},
	};
};
//this function makes columns from the data
function convertData(input) {
	const result = [];

	for (const key in input[0]) {
		if (key !== 'VENDOR_NAME' && key.includes('|')) {
			const keys = key.split('|');
			let currentLevel = result;

			for (let i = 0; i < keys.length; i++) {
				const currentKey = keys[i];
				const existingGroup = currentLevel.find(
					item => item.groupId === currentKey
				);

				if (existingGroup) {
					currentLevel = existingGroup.children;
				} else {
					const newGroup = {
						groupId: currentKey,
						headerName: currentKey,
					};

					if (i === keys.length - 1) {
						newGroup.field = key;
						newGroup.filterParams = {
							filterOptions: [
								'equals',
								'notEqual',
								'lessThan',
								'lessThanOrEqual',
								'greaterThan',
								'greaterThanOrEqual',
							],
						};
						newGroup.filter = 'agNumberColumnFilter';
						newGroup.enableRowGroup = false;
						newGroup.enableValue = true;
						newGroup.allowedAggFuncs = ['sum', 'min', 'max', 'avg'];
					} else {
						newGroup.children = [];
					}

					currentLevel.push(newGroup);
					currentLevel = newGroup.children;
				}
			}
		}
	}

	return result;
}
function formatNumericKeys(arr, substrings) {
	return arr.map(obj => {
		for (let key in obj) {
			if (substrings.some(substring => key.includes(substring))) {
				obj[key] = obj[key]?.toLocaleString();
			}
		}
		return obj;
	});
}
const createDatasource = (
	gridOptions,
	paramSetter,
	columnApi,
	hashSetter,
	fetchGridOptions,
	gridApi
) => {
	return {
		// called by the grid when more rows are required
		getRows: async params => {
			const object = {};
			object.gridOptions = gridOptions;
			object.client = await getGraphQLClientForPivotWithToken();
			const columns = object.gridOptions.columnDefs;
			localStorage.setItem('pivotmode', params.request.pivotMode);

			object.client
				.query(
					params.request.pivotMode
						? pivotquery(params.request, columns)
						: query(params.request, columns)
				)
				.then(response => {
					let rows = params.request.pivotMode
						? response.data.pivotData.length > 0 &&
						  response.data.pivotData[0].data === null
							? capitalizeFieldNames(response.data.pivotData)
							: (() => {
									return formatNumericKeys(
										JSON.parse(response.data.pivotData[0].data || '[]'),
										[
											'PO_BASE_SPEND',
											'NET_VALUE_BASE_GBP',
											'NET_VALUE_BASE_USD',
											'NET_VALUE_BASE_EUR',
										]
									);
							  })()
						: (() => {
								columnApi.setSecondaryColumns([]);
								return capitalizeFieldNames(response.data.agGridData);
						  })();
					paramSetter(params.request);
					const output = convertData(rows);
					var colDefHash = createColsHash(output);
					if (localStorage.getItem('currentColHash') !== colDefHash) {
						columnApi.setSecondaryColumns(output);
						localStorage.setItem('currentColHash', colDefHash);
					}

					localStorage.setItem('pivotmode', params.request.pivotMode);

					if (
						localStorage.getItem('pivotmode') === 'true' &&
						!localStorage.hasOwnProperty('defaultView')
					) {
						localStorage.setItem('defaultView', true);
						columnApi.setRowGroupColumns(['AP_L1Category']);
						columnApi.setValueColumns(['PO_BASE_SPEND']);
						gridApi.setSortModel([]);
					} else if (
						localStorage.hasOwnProperty('defaultView') &&
						localStorage.getItem('pivotmode') === 'false'
					) {
						localStorage.removeItem('defaultView');
						columnApi.setRowGroupColumns([]);
						columnApi.setValueColumns([]);
						gridApi.setSortModel([]);
					}

					let lastRow = -1;

					let newGridOptions = fetchGridOptions();

					if (rows.length <= newGridOptions.cacheBlockSize) {
						lastRow = params.request.startRow + rows.length;
					}
					params.successCallback(rows, lastRow);
				})
				.catch(err => {
					console.error(err);
					params.failCallback();
				});
		},
	};
};

export default createDatasource;
